// Push Notifications hook for Web Push
import { useState, useEffect, useCallback } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

// VAPID public key from environment
const VAPID_PUBLIC_KEY = import.meta.env['VITE_VAPID_PUBLIC_KEY'] as string | undefined

// Query keys for push notifications
export const pushNotificationKeys = {
  permission: () => ['push-notifications', 'permission'] as const,
  subscription: () => ['push-notifications', 'subscription'] as const,
}

// Permission states
export type PushPermissionState = 'default' | 'granted' | 'denied' | 'unsupported'

// Convert VAPID key from base64 URL to Uint8Array
function urlBase64ToUint8Array(base64String: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')

  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }

  return outputArray.buffer
}

// Check if push notifications are supported
export function isPushSupported(): boolean {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window &&
    !!VAPID_PUBLIC_KEY
  )
}

// Get current permission state
export function getPermissionState(): PushPermissionState {
  if (!isPushSupported()) return 'unsupported'
  return Notification.permission as PushPermissionState
}

/**
 * Hook to check push notification support and permission
 */
export function usePushPermission() {
  const [permission, setPermission] = useState<PushPermissionState>(getPermissionState)

  useEffect(() => {
    if (!isPushSupported()) return

    // Update permission state when it changes
    const handlePermissionChange = () => {
      setPermission(getPermissionState())
    }

    // Poll for permission changes (since there's no event listener)
    const interval = setInterval(handlePermissionChange, 1000)

    return () => clearInterval(interval)
  }, [])

  return {
    permission,
    isSupported: isPushSupported(),
    isGranted: permission === 'granted',
    isDenied: permission === 'denied',
    isDefault: permission === 'default',
  }
}

/**
 * Hook to manage push notification subscription
 */
export function usePushSubscription(
  restaurantId: string | null,
  userId?: string | undefined,
  assistantId?: string | undefined
) {
  const queryClient = useQueryClient()
  const { permission, isSupported } = usePushPermission()

  // Get current subscription
  const { data: subscription, isLoading: isLoadingSubscription } = useQuery({
    queryKey: pushNotificationKeys.subscription(),
    queryFn: async (): Promise<PushSubscription | null> => {
      if (!isSupported || permission !== 'granted') return null

      const registration = await navigator.serviceWorker.ready
      const sub = await registration.pushManager.getSubscription()

      return sub
    },
    enabled: isSupported && permission === 'granted',
    staleTime: 60 * 1000, // 1 minute
  })

  // Subscribe to push notifications
  const subscribeMutation = useMutation({
    mutationFn: async (): Promise<PushSubscription> => {
      if (!isSupported || !VAPID_PUBLIC_KEY) {
        throw new Error('Push notifications are not supported')
      }

      if (!restaurantId) {
        throw new Error('Restaurant ID is required')
      }

      // Request permission
      const permissionResult = await Notification.requestPermission()
      if (permissionResult !== 'granted') {
        throw new Error('Permission denied')
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready

      // Check for existing subscription
      let sub = await registration.pushManager.getSubscription()

      // If no subscription, create one
      if (!sub) {
        sub = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
        })
      }

      // Save subscription to database using the save_push_subscription RPC function
      // This function is a SECURITY DEFINER that handles RLS properly
      const subJson = sub.toJSON()
      const p256dhKey = subJson.keys?.['p256dh'] ?? ''
      const authKey = subJson.keys?.['auth'] ?? ''

      // Use the RPC function instead of direct table access
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase.rpc as any)('save_push_subscription', {
        p_endpoint: sub.endpoint,
        p_p256dh_key: p256dhKey,
        p_auth_key: authKey,
        p_restaurant_id: restaurantId,
        p_user_id: userId ?? null,
        p_assistant_id: assistantId ?? null,
        p_user_agent: navigator.userAgent,
      })

      if (error) {
        throw new Error(`Failed to save subscription: ${error.message}`)
      }

      return sub
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pushNotificationKeys.subscription() })
      queryClient.invalidateQueries({ queryKey: pushNotificationKeys.permission() })
    },
  })

  // Unsubscribe from push notifications
  const unsubscribeMutation = useMutation({
    mutationFn: async () => {
      if (!subscription) return

      // Remove from browser
      await subscription.unsubscribe()

      // Remove from database using the RPC function
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase.rpc as any)('remove_push_subscription', {
        p_endpoint: subscription.endpoint,
      })

      if (error) {
        console.error('Failed to remove subscription from database:', error)
        // Don't throw - browser subscription is already removed
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pushNotificationKeys.subscription() })
    },
  })

  return {
    subscription,
    isSubscribed: !!subscription,
    isLoading: isLoadingSubscription,
    subscribe: subscribeMutation.mutate,
    subscribeAsync: subscribeMutation.mutateAsync,
    isSubscribing: subscribeMutation.isPending,
    subscribeError: subscribeMutation.error,
    unsubscribe: unsubscribeMutation.mutate,
    unsubscribeAsync: unsubscribeMutation.mutateAsync,
    isUnsubscribing: unsubscribeMutation.isPending,
    unsubscribeError: unsubscribeMutation.error,
  }
}

/**
 * Main hook combining permission and subscription management
 */
export function usePushNotifications(
  restaurantId: string | null,
  options?: {
    userId?: string | undefined
    assistantId?: string | undefined
  }
) {
  const permissionInfo = usePushPermission()
  const subscriptionInfo = usePushSubscription(
    restaurantId,
    options?.userId,
    options?.assistantId
  )

  // Request permission without subscribing
  const requestPermission = useCallback(async (): Promise<NotificationPermission> => {
    if (!permissionInfo.isSupported) {
      throw new Error('Push notifications are not supported')
    }

    return await Notification.requestPermission()
  }, [permissionInfo.isSupported])

  // Enable push notifications (request permission and subscribe)
  const enable = useCallback(async () => {
    if (!permissionInfo.isSupported) {
      throw new Error('Push notifications are not supported')
    }

    if (permissionInfo.isDenied) {
      throw new Error('Notification permission was denied. Please enable it in browser settings.')
    }

    await subscriptionInfo.subscribeAsync()
  }, [permissionInfo.isSupported, permissionInfo.isDenied, subscriptionInfo])

  // Disable push notifications (unsubscribe)
  const disable = useCallback(async () => {
    await subscriptionInfo.unsubscribeAsync()
  }, [subscriptionInfo])

  return {
    // Permission state
    ...permissionInfo,

    // Subscription state
    subscription: subscriptionInfo.subscription,
    isSubscribed: subscriptionInfo.isSubscribed,
    isLoading: subscriptionInfo.isLoading,

    // Actions
    requestPermission,
    enable,
    disable,

    // Mutation states
    isEnabling: subscriptionInfo.isSubscribing,
    enableError: subscriptionInfo.subscribeError,
    isDisabling: subscriptionInfo.isUnsubscribing,
    disableError: subscriptionInfo.unsubscribeError,
  }
}
